import { ReactElement } from 'react';
import StatusButton from '../StatusButton';
import DropdownCustom from '../../controls/DropdownCustom';
import {
  ActionData,
  ActionsTypes,
  AlertRule,
  Option,
  PriorityList
} from '../../constants/telosair-interfaces';
import { getParameterByName } from '../../_helpers/globalHelper';
import { useData } from '../../context/data.context';

const options: Option<ActionsTypes>[] = [
  { id: 'edit', name: 'Edit Rule' },
  { id: 'delete', name: 'Delete Rule' }
];

type AlertRuleRowProps = {
  alertRule: AlertRule;
  handleAction: (data: ActionData<AlertRule>) => void;
};

/**
 * Notes page renders list of Notes.
 * @param status - Status of the alert.
 * @returns - Priority of the alert.
 */
function getPriority(status: string): PriorityList {
  switch (status) {
    case 'Active':
      return PriorityList.low;
    case 'Warning':
      return PriorityList.medium;
    case 'Critical':
      return PriorityList.high;
    default:
      return PriorityList.none;
  }
}

/**
 * A component to display device data in a grid.
 * @param props - Device Row Properties.
 * @returns A React element representing the DeviceRow component.
 */
export default function AlertRuleRow(props: AlertRuleRowProps): ReactElement {
  const { alertRule, handleAction } = props;
  const { parameters } = useData();
  /**
   * Function to handle action.
   * @param type - Type of the action edit or delete.
   */
  const onAction = (type: ActionsTypes): void => {
    handleAction({ type, data: alertRule });
  };

  const parameter =
    alertRule.parameterName === 'sensor_status'
      ? { shortName: 'Sensor status' }
      : getParameterByName(alertRule.parameterName, parameters);
  const devices = alertRule.deviceId?.split(',');
  return (
    <tr className="bg-white dark:bg-container-500 text-gray-500 dark:text-gray-300 border-b border-b-gray-300 dark:border-b-gray-900">
      <td className="px-6">#{alertRule.id}</td>
      <td className="py-3 text-center flex items-center justify-center">
        <div className="w-[90px]">
          <StatusButton
            data={{
              priority: getPriority(alertRule?.status || ''),
              property_value: alertRule?.status || '',
              property_name: alertRule?.status
            }}
          />
        </div>
      </td>
      <td className="py-3 text-center">
        <div>
          <div>
            {alertRule.deviceId
              ? devices?.map((item, index) => {
                  if (item === '0') {
                    return 'All';
                  }
                  return `${item}${index !== devices.length - 1 ? ', ' : ''}`;
                })
              : '-'}
          </div>
          <div className="text-xs text-gray-500">-</div>
        </div>
      </td>
      <td className="py-3 text-center">{alertRule.priority}</td>
      <td className="py-3 text-center">{parameter?.shortName}</td>
      <td className="py-3 text-center">{alertRule.notification}</td>
      <td className="py-3 text-center">
        <DropdownCustom<ActionsTypes>
          controlPlaceholder="Action"
          buttons={options}
          onClick={onAction}
        />
      </td>
    </tr>
  );
}
